import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Sidebar from '../Sidebar/Sidebar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGear, faUserShield, faKey, faSave, faCamera, faCalendarCheck, faCheck, faSignOutAlt, faEnvelopeOpenText } from '@fortawesome/free-solid-svg-icons';
import { buildApiUrl } from '../../config/api.config';

const ChefSettings = ({ showAlert }) => {
  const [user, setUser] = useState({});
  const [passwords, setPasswords] = useState({ current: '', new: '', confirm: '' });
  const [loading, setLoading] = useState(false);
  const [photoLoading, setPhotoLoading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(buildApiUrl("users/getuser"), {}, {
        headers: { 'auth-token': token }
      });
      setUser(response.data);
    } catch (err) {
      console.error("Error fetching profile:", err);
    } finally {
      setLoading(false);
    }
  };



  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      if (showAlert) showAlert("Please select an image file", "danger");
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      if (showAlert) showAlert("Image size should be less than 2MB", "danger");
      return;
    }

    setPhotoLoading(true);
    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.put(
          buildApiUrl("users/update-profile-photo"),
          { profilePhoto: reader.result },
          { headers: { 'auth-token': token } }
        );

        if (response.data.success) {
          const updatedUser = { ...user, profilePhoto: reader.result };
          setUser(updatedUser);
          localStorage.setItem('userDetails', JSON.stringify(updatedUser));
          if (showAlert) showAlert("Profile photo updated!", "success");
        }
      } catch (error) {
        console.error("Error uploading photo:", error);
        if (showAlert) showAlert("Failed to upload photo", "danger");
      } finally {
        setPhotoLoading(false);
      }
    };
    reader.readAsDataURL(file);
  };



  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (!passwords.current || !passwords.new || !passwords.confirm) {
        if(showAlert) showAlert("Please fill all password fields", "danger");
        return;
    }
    if (passwords.new !== passwords.confirm) {
        if(showAlert) showAlert("New passwords do not match", "danger");
        return;
    }
    if (passwords.new.length < 5) {
        if(showAlert) showAlert("New password must be at least 5 characters long", "warning");
        return;
    }
    
    setLoading(true);
    try {
        const token = localStorage.getItem('token');
        const response = await axios.put(buildApiUrl("users/change-password"), {
            currentPassword: passwords.current,
            newPassword: passwords.new
        }, {
            headers: { 'auth-token': token }
        });

        if (response.data.success) {
            if(showAlert) showAlert("Password updated successfully", "success");
            setPasswords({ current: '', new: '', confirm: '' });
        }
    } catch (error) {
        console.error("Error changing password:", error);
        if(showAlert) showAlert(error.response?.data?.message || "Failed to update password", "danger");
    } finally {
        setLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex', height: '100vh', background: '#0a0a0c', color: 'white' }}>
      <Sidebar />
      <div style={{ flex: 1, padding: '2rem', overflowY: 'auto' }}>
        <h1 style={{ fontSize: '2rem', fontWeight: '800', marginBottom: '2rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <FontAwesomeIcon icon={faGear} style={{ color: '#ff4135' }} />
            Chef Settings
        </h1>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem' }}>
            {/* Profile Card */}
            <div style={{ background: '#1c1c1f', padding: '2rem', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.05)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', marginBottom: '1.5rem' }}>
                    <div 
                        onClick={() => fileInputRef.current?.click()}
                        style={{ 
                            width: '80px', 
                            height: '80px', 
                            background: user.profilePhoto ? `url(${user.profilePhoto}) center/cover` : 'rgba(255,65,53,0.1)', 
                            color: '#ff4135', 
                            borderRadius: '50%', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center', 
                            fontSize: '2rem',
                            cursor: 'pointer',
                            position: 'relative',
                            overflow: 'hidden',
                            border: '3px solid rgba(255,65,53,0.3)'
                        }}
                    >
                        {!user.profilePhoto && <FontAwesomeIcon icon={faUserShield} />}
                        <div style={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            background: 'rgba(0,0,0,0.7)',
                            padding: '0.3rem',
                            fontSize: '0.6rem',
                            textAlign: 'center',
                            color: 'white'
                        }}>
                            {photoLoading ? '...' : <FontAwesomeIcon icon={faCamera} />}
                        </div>
                    </div>
                    <input 
                        type="file" 
                        ref={fileInputRef}
                        onChange={handlePhotoUpload}
                        accept="image/*"
                        style={{ display: 'none' }}
                    />
                    <div>
                        <h2 style={{ margin: 0 }}>{user.name}</h2>
                        <p style={{ margin: '0.2rem 0 0', color: 'rgba(255,255,255,0.5)', textTransform: 'capitalize' }}>
                            {user.staffRole} Account
                        </p>
                    </div>
                </div>
                <div style={{ background: 'rgba(0,0,0,0.2)', padding: '1rem', borderRadius: '8px', marginBottom: '1.5rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)' }}>EMAIL ADDRESS</label>
                    <div style={{ fontSize: '1rem' }}>{user.email}</div>
                </div>


            </div>

            {/* Security Settings */}
            <div style={{ background: '#1c1c1f', padding: '2rem', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.05)' }}>
                <h3 style={{ marginTop: 0, display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
                    <FontAwesomeIcon icon={faKey} style={{ color: '#ff4135' }} />
                    Change Password
                </h3>
                <form onSubmit={handlePasswordChange} style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '1.5rem' }}>
                    <input 
                        type="password" 
                        placeholder="Current Password"
                        value={passwords.current}
                        onChange={e => setPasswords({...passwords, current: e.target.value})}
                        style={{ padding: '0.8rem', background: 'rgba(255,255,255,0.05)', border: 'none', borderRadius: '8px', color: 'white' }} 
                    />
                    <input 
                        type="password" 
                        placeholder="New Password"
                        value={passwords.new}
                        onChange={e => setPasswords({...passwords, new: e.target.value})}
                        style={{ padding: '0.8rem', background: 'rgba(255,255,255,0.05)', border: 'none', borderRadius: '8px', color: 'white' }} 
                    />
                    <input 
                        type="password" 
                        placeholder="Confirm New Password"
                        value={passwords.confirm}
                        onChange={e => setPasswords({...passwords, confirm: e.target.value})}
                        style={{ padding: '0.8rem', background: 'rgba(255,255,255,0.05)', border: 'none', borderRadius: '8px', color: 'white' }} 
                    />
                    <button 
                        type="submit" 
                        disabled={loading}
                        style={{ padding: '0.8rem', background: '#ff4135', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', marginTop: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
                    >
                        {loading ? 'Saving...' : <><FontAwesomeIcon icon={faSave} /> Update Password</>}
                    </button>
                </form>
            </div>
        </div>
      </div>
    </div>
  );
};

export default ChefSettings;
